import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {useCallback, useEffect, useRef, useState} from "react";
import {addCompany, deleteCompany, getAllCompanies, getCompanyById, updateCompany} from "../../api/company/companyApiClient";
import SoftAlert from "../../components/Alert";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import AddEditCompanyDialog from "./components/AddEditCompanyDialog";
import ViewCompanyDialog from "./components/ViewCompanyDialog";
import SoftInput from "../../components/SoftInput";
import SoftButton from "components/SoftButton";
import {IconButton} from "@mui/material";
import {Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon} from "@mui/icons-material";
import EnhancedTable from "../../components/EnhancedTable";
import DataTable, {renderPill} from "../../components/DataTable";
import {getStatusColor, getStatusDisplayName} from "../../utils/statusUtils";
import {handleApiError, showDeleteSuccess} from "../../utils/notificationUtils";
import {useRayzayUIController} from "../../context";

function Overview() {
    const [controller] = useRayzayUIController();
    const {loggedInUserDetail} = controller;
    const isCompanyAdmin = loggedInUserDetail?.userRole === "COMPANY_ADMIN";
    const [companies, setCompanies] = useState([]);
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState(null);
    const [companyToEdit, setCompanyToEdit] = useState(null);
    const [companyToView, setCompanyToView] = useState(null);
    const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const hasLoadedRef = useRef(false);

    const columns = [
        {
            name: "Reference Number",
            value: "referenceNumber",
            align: "left",
            width: "15",
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="medium">
                    {row.referenceNumber || "-"}
                </SoftTypography>
            ),
        },
        {
            name: "Name",
            value: "name",
            align: "left",
            width: "15",
            renderCell: (row) => (
                <SoftTypography
                    variant="button"
                    fontWeight="medium"
                    sx={{maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                >
                    {row.name || ""}
                </SoftTypography>
            ),
        },
        {name: "Phone No", value: "phoneNo", align: "left", width: "12"},
        {
            name: "Website",
            value: "webUrl",
            align: "left",
            width: "15",
            renderCell: (row) => (
                row.webUrl ? (
                    <SoftTypography
                        component="a"
                        href={row.webUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="button"
                        fontWeight="medium"
                        color="primary"
                        sx={{
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            "&:hover": {textDecoration: "underline"},
                        }}
                    >
                        {row.webUrl}
                    </SoftTypography>
                ) : (
                    <SoftTypography variant="button" fontWeight="medium" color="text">
                        -
                    </SoftTypography>
                )
            ),
        },
        {name: "Max Branches", value: "maxNoOfBranches", align: "left", width: "10"},
        {
            name: "Status",
            value: "status",
            align: "center",
            width: "10",
            renderCell: (row) => renderPill(getStatusDisplayName(row.status), getStatusColor(row.status)),
        },
        {
            name: "Actions",
            value: "action",
            align: "center",
            width: "15",
            renderCell: (row) => (
                <>
                    <IconButton color="primary" size="small" onClick={() => handleViewClick(row)}>
                        <VisibilityIcon fontSize="small"/>
                    </IconButton>
                    <IconButton color="primary" size="small" onClick={() => handleEditClick(row)}>
                        <EditIcon fontSize="small"/>
                    </IconButton>
                    {!isCompanyAdmin && (
                        <IconButton color="error" size="small" onClick={() => handleDeleteClick(row)}>
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    )}
                </>
            ),
        },
    ];

    const loadCompanies = useCallback(async () => {
        // Prevent duplicate calls
        if (hasLoadedRef.current) {
            return;
        }
        
        try {
            hasLoadedRef.current = true;
            setIsLoading(true);
            setError(null);
            
            const response = await getAllCompanies();
            const companiesData = response.data || [];
            console.log('Companies loaded from API:', companiesData);
            setCompanies(companiesData);
            setFilteredCompanies(companiesData);
        } catch (error) {
            console.error('Error loading companies:', error);
            handleApiError(error, 'Failed to load companies. Please try again.');
            setError('Failed to load companies. Please try again.');
            setShowAlert(true);
            setCompanies([]);
            setFilteredCompanies([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    const handleAddCompany = () => {
        setCompanyToEdit(null);
        setIsEdit(false);
        setEditAddDialogOpen(true);
    };

    const handleViewClick = (company) => {
        setCompanyToView(company);
        setViewDialogOpen(true);
    };

    const handleEditClick = async (row) => {
        try {
            setIsLoading(true);
            // Fetch the latest company data by ID to ensure we have all the data
            const response = await getCompanyById(row.companyId);
            const companyData = response.data;
            console.log('Fetched company data for edit:', companyData);
            if (companyData) {
                // Set company data first, then set edit mode, then open dialog
                setCompanyToEdit(companyData);
                setIsEdit(true);
                // Use setTimeout to ensure state updates are processed before opening dialog
                setTimeout(() => {
                    setEditAddDialogOpen(true);
                }, 0);
            }
        } catch (error) {
            console.error('Error loading company for edit:', error);
            handleApiError(error, 'Failed to load company data for editing');
            setError('Failed to load company data for editing');
            setShowAlert(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (company) => {
        setCompanyToDelete(company);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteCompany(companyToDelete.companyId);
            showDeleteSuccess('Company deleted successfully');
            hasLoadedRef.current = false; // Reset to allow reload after delete
            await loadCompanies();
            setDeleteDialogOpen(false);
            setCompanyToDelete(null);
        } catch (err) {
            handleApiError(err, 'Failed to delete company');
            setError("Failed to delete company");
            setShowAlert(true);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
    };

    const handleOnSubmit = async (formData) => {
        try {
            if (isEdit) {
                await updateCompany(companyToEdit.companyId, formData);
            } else {
                await addCompany(formData);
            }
            hasLoadedRef.current = false; // Reset to allow reload after save
            await loadCompanies();
            setEditAddDialogOpen(false);
            setCompanyToEdit(null);
        } catch (err) {
            handleApiError(err, 'Failed to save company');
            setError('Failed to save company. Please try again.');
            setShowAlert(true);
            throw err; // Re-throw to let dialog handle it
        }
    };

    const handleCloseDialog = () => {
        setDeleteDialogOpen(false);
        setEditAddDialogOpen(false);
        setViewDialogOpen(false);
        setCompanyToEdit(null);
        setCompanyToDelete(null);
        setCompanyToView(null);
    };

    const handleFilterCompany = (value) => {
        let filtered = companies.filter(company => {
            const searchValue = value.toLowerCase();
            return (company.name && company.name.toLowerCase().includes(searchValue)) ||
                (company.description && company.description.toLowerCase().includes(searchValue)) ||
                (company.address && company.address.toLowerCase().includes(searchValue)) ||
                (company.webUrl && company.webUrl.toLowerCase().includes(searchValue)) ||
                (company.phoneNo && company.phoneNo.toLowerCase().includes(searchValue));
        });
        setFilteredCompanies(filtered);
    };

    // Transform companies data for table
    const tableRows = filteredCompanies.map(company => ({
        companyId: company.companyId,
        referenceNumber: company.referenceNumber || '',
        name: company.name,
        description: company.description || '',
        address: company.address || '',
        webUrl: company.webUrl || '',
        phoneNo: company.phoneNo || '',
        maxNoOfBranches: company.maxNoOfBranches !== null && company.maxNoOfBranches !== undefined ? company.maxNoOfBranches : '',
        status: company.status || 'ACTIVE', // Default to ACTIVE for existing records
        hasBorder: true
    }));

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                    <SoftBox display="flex" alignItems="center" gap={2} flexShrink={0}>
                        <SoftTypography variant="h6" fontWeight="medium">
                            Companies
                        </SoftTypography>
                    </SoftBox>
                    <SoftBox flexGrow={1} maxWidth={560}>
                        <SoftInput
                            placeholder="Type here to filter by name, description, address, URL or phone"
                            icon={{component: "search", direction: "left"}}
                            onChange={(event) => handleFilterCompany(event.target.value)}
                        />
                    </SoftBox>
                    {!isCompanyAdmin && (
                        <SoftButton
                            variant="gradient"
                            color="primary"
                            onClick={handleAddCompany}
                            sx={{flexShrink: 0}}
                        >
                            Add Company
                        </SoftButton>
                    )}
                </SoftBox>
            </SoftBox>
            <SoftBox p={2} width="100%">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <EnhancedTable
                            TableComponent={DataTable}
                            isLoading={isLoading}
                            data={tableRows}
                            loadingMessage="Loading companies..."
                            noDataMessage="No companies found"
                            columns={columns}
                        />
                    </Grid>
                </Grid>
            </SoftBox>
            {error && (
                <SoftAlert
                    color="error"
                    dismissible={false}
                    timeout={10}
                    mount={showAlert}
                    onClose={() => {
                        setError(null);
                        setShowAlert(false);
                    }}
                >
                    {error}
                </SoftAlert>
            )}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onDelete={handleDeleteConfirm}
                warningMessage={`Are you sure you want to delete ${companyToDelete?.name || ""}? This action cannot be undone.`}
            />
            <AddEditCompanyDialog
                key={companyToEdit?.companyId || 'new'}
                open={editAddDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleOnSubmit}
                company={companyToEdit}
                isEdit={isEdit}
            />
            <ViewCompanyDialog
                open={viewDialogOpen}
                onClose={handleCloseDialog}
                company={companyToView}
            />
        </DashboardLayout>
    );
}

export default Overview;

